import React from 'react';
import Icon from '../../../components/AppIcon';

const FlagModal = ({ isOpen, onClose, rotation, onConfirm }) => {
  if (!isOpen || !rotation) return null;

  const FLAGS = [
    { id: 'reroute', label: 'Reroute', color: 'bg-accent-purple text-white', icon: 'RefreshCw', description: 'Schedule changed due to operational needs' },
    { id: 'sick', label: 'Sick', color: 'bg-error text-white', icon: 'Heart', description: 'Sick leave taken during rotation' },
    { id: 'apd_pd', label: 'APD/PD', color: 'bg-accent-blue text-white', icon: 'Calendar', description: 'Authorised Personal Drop / Personal Day' },
    { id: '23k', label: '23K', color: 'bg-accent-cyan text-white', icon: 'DollarSign', description: 'Contract reference 23K' },
    { id: 'ws', label: 'WS', color: 'bg-accent-green text-white', icon: 'Clock', description: 'White Slip' },
    { id: 'gs', label: 'GS', color: 'bg-success text-white', icon: 'CheckCircle', description: 'Green Slip' },
    { id: 'sl', label: 'SL', color: 'bg-warning text-black', icon: 'AlertCircle', description: 'Silver Slip' },
    { id: 'qs', label: 'QS', color: 'bg-accent-pink text-white', icon: 'HelpCircle', description: 'Quick Slip' },
    { id: 'vac', label: 'VAC', color: 'bg-primary text-white', icon: 'Palmtree', description: 'Vacation time' },
    { id: 'other', label: 'Other', color: 'bg-muted text-foreground', icon: 'MoreHorizontal', description: 'Other special condition' }
  ];

  const existingFlags = rotation.flags || [];

  const handleFlagClick = (flag) => {
    // Check if flag already exists
    if (existingFlags.includes(flag.id)) {
      alert(`${flag.label} flag is already applied to this rotation`);
      return;
    }
    onConfirm(rotation, flag.id);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-lg border border-accent-cyan/20 shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-accent-cyan/20">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Add Flag</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Rotation {rotation.rotationNumber} - {rotation.employeeName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-accent-cyan/10 text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" className="w-5 h-5" />
          </button>
        </div>

        {/* Existing Flags */}
        {existingFlags.length > 0 && (
          <div className="p-6 border-b border-accent-cyan/20 bg-accent-cyan/5">
            <div className="text-sm font-medium text-foreground mb-3">Current Flags:</div>
            <div className="flex flex-wrap gap-2">
              {existingFlags.map((flagId) => {
                const flag = FLAGS.find(f => f.id === flagId);
                if (!flag) return null;
                return (
                  <span 
                    key={flagId}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium ${flag.color}`}
                  >
                    <Icon name={flag.icon} className="w-4 h-4" />
                    {flag.label}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Flag Options */}
        <div className="p-6">
          <div className="text-sm font-medium text-muted-foreground mb-4">Select a flag to add:</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {FLAGS.map((flag) => {
              const isApplied = existingFlags.includes(flag.id);
              return (
                <button
                  key={flag.id}
                  onClick={() => handleFlagClick(flag)}
                  disabled={isApplied}
                  className={`
                    flex items-start gap-3 p-4 rounded-lg border-2 text-left transition-all
                    ${isApplied 
                      ? 'border-muted bg-muted/20 opacity-50 cursor-not-allowed' 
                      : 'border-accent-cyan/20 hover:border-accent-cyan hover:bg-accent-cyan/5 cursor-pointer'
                    }
                  `}
                >
                  <div className={`p-2 rounded-lg ${flag.color} flex-shrink-0`}>
                    <Icon name={flag.icon} className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-foreground mb-1">
                      {flag.label}
                      {isApplied && <span className="ml-2 text-xs text-muted-foreground">(Applied)</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">{flag.description}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-accent-cyan/20 bg-card-alt">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-accent-cyan/20 text-foreground hover:bg-accent-cyan/10 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default FlagModal;
